import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_SpeechToText = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/speech-to-text',
			name: 'Speech to Text AI: Turn Voice Recordings into Written Words',
			description:
				"Convert voice to text from memos, dictated ideas, and recorded talks. Upload a file, and Lift's AI writes down what you said. Try Lift today!",
			url: 'https://liftapp.ai/speech-to-text',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/speech-to-text#product' },
				{ '@id': 'https://liftapp.ai/speech-to-text#howto' },
				{ '@id': 'https://liftapp.ai/speech-to-text#benefits' },
				{ '@id': 'https://liftapp.ai/speech-to-text#faq' },
				{ '@id': 'https://liftapp.ai/speech-to-text#why-lift' },
				{ '@id': 'https://liftapp.ai/speech-to-text#explore-tools' },
				{ '@id': 'https://liftapp.ai/speech-to-text#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/speech-to-text#product',
			name: 'Online Speech to Text Converter',
			description:
				'Upload a memo or recorded talk, and AI turns your spoken words into notes you can copy or download. Try Lift today!',
			url: 'https://liftapp.ai/speech-to-text',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/speech-to-text#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload a voice recording',
					text: 'Add a memo, dictated note, or any spoken audio or video file from your device.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Confirm the spoken language',
					text: 'Auto detects it for you, or choose one manually and switch audio cues on or off.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Turn speech into text',
					text: "Tap Generate, and Lift's AI writes out the recording, saving it in My Transcriptions.",
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Clean up and save your notes',
					text: 'Remove filler words and repeats, then copy the result or download it as TXT or PDF.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/speech-to-text#benefits',
			name: 'Who can benefit from Speech to Text?',
			description:
				'Lift Speech to Text helps writers, busy professionals, multilingual speakers, and public speakers turn voice recordings into editable notes.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Writers and bloggers — Dictate drafts on a walk, then upload the recording and get text ready to edit.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Busy professionals — Turn voice memos with ideas and to-dos into notes you can paste into any app.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'People who prefer talking to typing — Speak your thoughts naturally and let AI handle the writing part.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Multilingual speakers — Record in your native language and let AI detect it automatically before transcribing.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Public speakers — Transcribe rehearsals of talks and sermons to tighten the wording before the big day.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/speech-to-text/benefits.webp',
				name: 'Speech to Text by Lift',
				description:
					'Laptop on a home desk showing a speech transcript with Summarize and Clean up options',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/speech-to-text#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Speech to Text?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Speech to Text is an AI tool that turns spoken words in your recordings into readable notes and documents. Use it for voice memos, dictation, speeches, and any other talk you have recorded.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Speech to Text work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload an audio or video file, leave the language on Auto or select it, and tap Generate. Once the transcript is ready, copy it, clean it up, or download it.',
					},
				},
				{
					'@type': 'Question',
					name: 'Do I have to choose the language before converting?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'No. The setting is on Auto by default, so AI detects it for you. Set it manually to give the model a clear hint on tricky recordings.',
					},
				},
				{
					'@type': 'Question',
					name: 'How can I save the converted text?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Use Copy to send everything to your clipboard, or Download to get a TXT or PDF file. If you also created a summary, export it on its own or together with the transcription.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my voice recordings to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your recordings securely and uses them only to generate your transcript.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/speech-to-text#why-lift',
			name: 'Why choose Lift for speech to text',
			description:
				"Lift's online speech to text converter handles lectures, study notes, and quick recaps in one affordable tool on iOS, Android, and Web.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/speech-to-text/whyLift.webp',
				name: 'Why use Lift Speech to Text',
				description:
					"Lecture recording turned into a short summary with key points by Lift's speech to text AI",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/speech-to-text#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/speech-to-text#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Speech to Text',
					item: 'https://liftapp.ai/speech-to-text',
				},
			],
		},
	],
};
